import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useConfirm } from '../components/ConfirmModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { pending: '#f59e0b', approved: '#22c55e', rejected: '#ef4444' };

const S = {
  page:     { fontFamily:"'Sora',sans-serif" },
  title:    { fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
  sub:      { fontSize: 13, color: '#64748b', marginBottom: 24 },
  card:     { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px #0000000a' },
  statGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16, marginBottom:28 },
  stat:     { background:'#fff', borderRadius:12, padding:'16px 20px', border:'1px solid #e2e8f0', textAlign:'center', boxShadow:'0 2px 8px #0000000a' },
  btn:      { padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13 },
  table:    { width:'100%', borderCollapse:'collapse' },
  th:       { padding:'10px 14px', background:'#f8fafc', color:'#475569', fontSize:12, fontWeight:600, textAlign:'left', textTransform:'uppercase', letterSpacing:0.5 },
  td:       { padding:'12px 14px', borderBottom:'1px solid #f1f5f9', fontSize:13, color:'#475569' },
  badge:    (c) => ({ display:'inline-block', padding:'2px 10px', borderRadius:20, background:c+'22', color:c, fontSize:11, fontWeight:600 }),
  input:    { width:'100%', padding:'9px 12px', background:'#fff', border:'2px solid #e2e8f0', borderRadius:8, color:'#0f172a', fontSize:13, outline:'none', boxSizing:'border-box' },
  label:    { fontSize:12, color:'#475569', marginBottom:4, display:'block', fontWeight:600 },
  modal:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  mbox:     { background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:480, boxShadow:'0 24px 80px rgba(0,0,0,0.15)' },
};

const today = () => new Date().toISOString().split('T')[0];

export default function WFHRequests() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';
  const confirm = useConfirm();

  const [requests, setRequests]   = useState([]);
  const [stats,    setStats]      = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({ fromDate: today(), toDate: today(), reason: '', notes: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const [rRes, sRes] = await Promise.all([
        axios.get(`${API}/wfh`, { headers: headers(), params }),
        isAdmin ? axios.get(`${API}/wfh/stats`, { headers: headers() }) : Promise.resolve(null),
      ]);
      setRequests(rRes.data.data || []);
      if (sRes) setStats(sRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wfh`, form, { headers: headers() });
      setShowModal(false);
      setForm({ fromDate: today(), toDate: today(), reason: '', notes: '' });
      load();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const handleAction = async (type) => {
    try {
      await axios.put(`${API}/wfh/${actionModal.id}/status`,
        { status: type, rejectReason },
        { headers: headers() }
      );
      setActionModal(null);
      setRejectReason('');
      load();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Cancel this request?', { title: 'Cancel Request?', variant: 'warning' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/wfh/${id}`, { headers: headers() });
      load();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '-';

  return (
    <div style={S.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={S.title}>Work From Home</h1>
          <div style={S.sub}>Manage WFH requests and approvals</div>
        </div>
        <button style={{ ...S.btn, background:'var(--pm-gradient)', color:'#fff' }} onClick={() => setShowModal(true)}>
          + Request WFH
        </button>
      </div>

      {/* Stats — admin */}
      {isAdmin && stats && (
        <div style={S.statGrid}>
          {[
            { l:'Total',    v: stats.total,    c:'var(--pm-primary)' },
            { l:'Pending',  v: stats.pending,  c:'#f59e0b' },
            { l:'Approved', v: stats.approved, c:'#22c55e' },
            { l:'Rejected', v: stats.rejected, c:'#ef4444' },
          ].map(s => (
            <div key={s.l} style={S.stat}>
              <div style={{ fontSize:28, fontWeight:800, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <select style={{ ...S.input, width:180 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? <div style={{ color:'#94a3b8', textAlign:'center', padding:40 }}>Loading...</div> : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {isAdmin && <th style={S.th}>Employee</th>}
                <th style={S.th}>From</th>
                <th style={S.th}>To</th>
                <th style={S.th}>Days</th>
                <th style={S.th}>Reason</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Applied On</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && (
                <tr><td colSpan={isAdmin?8:7} style={{ ...S.td, textAlign:'center', color:'#94a3b8', padding:32 }}>
                  No WFH requests found.
                </td></tr>
              )}
              {requests.map(r => (
                <tr key={r.id}>
                  {isAdmin && (
                    <td style={S.td}>
                      <div style={{ fontWeight:600, color:'#0f172a' }}>{r.employee?.name}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{r.employee?.position || r.employee?.email}</div>
                    </td>
                  )}
                  <td style={S.td}>{fmt(r.fromDate)}</td>
                  <td style={S.td}>{fmt(r.toDate)}</td>
                  <td style={{ ...S.td, fontWeight:700, color:'var(--pm-primary)' }}>{r.days}</td>
                  <td style={{ ...S.td, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.reason || <span style={{ color:'#cbd5e1' }}>—</span>}
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(STATUS_COLOR[r.status] || '#94a3b8')}>{r.status}</span>
                  </td>
                  <td style={S.td}>{fmt(r.createdAt)}</td>
                  <td style={S.td}>
                    <div style={{ display:'flex', gap:6 }}>
                      {isAdmin && r.status === 'pending' && (
                        <>
                          <button style={{ ...S.btn, background:'#dcfce7', color:'#22c55e', padding:'4px 10px' }}
                            onClick={() => { setActionModal(r); handleAction('approved'); }}>
                            Approve
                          </button>
                          <button style={{ ...S.btn, background:'#fee2e2', color:'#ef4444', padding:'4px 10px' }}
                            onClick={() => setActionModal({ ...r, _action: 'reject' })}>
                            Reject
                          </button>
                        </>
                      )}
                      {!isAdmin && r.status === 'pending' && r.employeeId === user.id && (
                        <button style={{ ...S.btn, background:'#fee2e2', color:'#ef4444', padding:'4px 10px' }}
                          onClick={() => handleDelete(r.id)}>
                          Cancel
                        </button>
                      )}
                      {r.status === 'rejected' && r.rejectReason && (
                        <span style={{ fontSize:11, color:'#ef4444', fontStyle:'italic' }}>{r.rejectReason}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quick Approve handler */}
      {actionModal && !actionModal._action && (() => { handleAction('approved'); return null; })()}

      {/* Reject Modal */}
      {actionModal?._action === 'reject' && (
        <div style={S.modal}>
          <div style={S.mbox}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:16 }}>Reject WFH Request</div>
            <p style={{ color:'#64748b', fontSize:13, marginBottom:14 }}>
              <strong style={{ color:'#0f172a' }}>{actionModal.employee?.name}</strong> — {fmt(actionModal.fromDate)} to {fmt(actionModal.toDate)} ({actionModal.days} day{actionModal.days > 1 ? 's' : ''})
            </p>
            <label style={S.label}>Reason for Rejection</label>
            <textarea style={{ ...S.input, height:80, resize:'vertical' }}
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="Explain why..." />
            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button style={{ ...S.btn, background:'#ef4444', color:'#fff', flex:1 }}
                onClick={() => handleAction('rejected')}>Confirm Reject</button>
              <button style={{ ...S.btn, background:'#f1f5f9', color:'#64748b', flex:1 }}
                onClick={() => { setActionModal(null); setRejectReason(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Request Modal */}
      {showModal && (
        <div style={S.modal}>
          <div style={S.mbox}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:20 }}>Request Work From Home</div>
            <form onSubmit={handleSubmit}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={S.label}>From Date *</label>
                  <input type="date" style={S.input} value={form.fromDate}
                    min={today()}
                    onChange={e => setForm(f => ({...f, fromDate: e.target.value, toDate: e.target.value}))} required />
                </div>
                <div>
                  <label style={S.label}>To Date *</label>
                  <input type="date" style={S.input} value={form.toDate}
                    min={form.fromDate}
                    onChange={e => setForm(f => ({...f, toDate: e.target.value}))} required />
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={S.label}>Reason *</label>
                <textarea style={{ ...S.input, height:80, resize:'vertical' }}
                  value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                  placeholder="Why do you need to WFH?" required />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={S.label}>Additional Notes</label>
                <textarea style={{ ...S.input, height:60, resize:'vertical' }}
                  value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  placeholder="Any additional info..." />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" style={{ ...S.btn, background:'var(--pm-gradient)', color:'#fff', flex:1 }}>Submit Request</button>
                <button type="button" style={{ ...S.btn, background:'#f1f5f9', color:'#64748b', flex:1 }}
                  onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
